import { Link } from "react-router";
import {
  plannedSectionsFor,
  type PlannedTabKey,
} from "@shared-core/domain/planned-sections";
import {
  isRouteEnabled,
  paths,
  type CustomerTab,
  type RouteId,
} from "@shared-core/routing/routes";
import { cn } from "../../primitives/utils";

/** A built tab, or a planned one (PLANNED_SECTIONS) while previews are on. */
export type CustomerTabKey = CustomerTab | PlannedTabKey<"customer">;

interface CustomerTabsProps {
  /** Customer id — the scope param the estimates and jobs lists filter on. */
  customerId: string;
  /** Tharstern account code — the scope param the catalogue reads; the page's id (its code) until it loads. */
  customerCode?: string | null;
  /** Shown in the catalogue's own scope chip so it can label itself. */
  customerName?: string | null;
  /** The customer's CIS code (`CUS-…`), the key its own pages' URLs carry; the id until it loads. */
  cisCustomerCode?: string | null;
  current: CustomerTabKey;
}

interface TabLink {
  tab: CustomerTabKey;
  route: RouteId;
  label: string;
  to: string;
  planned?: boolean;
}

/**
 * The customer's tabs (docs/features/journey-visual-language/01_proposal.md
 * §5.8): Overview · Catalogue · Estimates · Quotes · Orders · Jobs ·
 * Settings. Estimates are Tharstern's (read-only) and Quotes are CIS's; Orders
 * and Jobs are the two tracks — CIS orders, and Tharstern's jobs made in-house — then
 * Settings, how the customer's payer pays (credit control), then Invoices,
 * the customer's pro-forma, invoices and credit notes (the sales ledger). Stock
 * is a planned tab (PLANNED_SECTIONS), appended muted while `previews` is on,
 * until stock control builds it; each tab shows only where its route's feature
 * is on.
 *
 * Overview, Quotes, Orders, Settings and Invoices render inside the customer's frame. Catalogue,
 * Estimates and Jobs open today's lists scoped to the customer, which leave
 * the frame: those pages are not built to sit inside it yet.
 *
 * No counts on purpose. The Pulse card already states live estimates and
 * jobs, and a second, differently-scoped number beside the same nouns is how
 * two figures for "the same thing" start disagreeing.
 */
export function CustomerTabs({
  customerId,
  customerCode,
  customerName,
  cisCustomerCode,
  current,
}: CustomerTabsProps) {
  // The customer's own pages (Overview, Quotes, Orders, Settings, Invoices) are keyed by its CIS code.
  const pageKey = cisCustomerCode ?? customerId;
  const tabs: TabLink[] = [
    {
      tab: "overview",
      route: "customerDetail",
      label: "Overview",
      to: paths.customerDetail({ customerId: pageKey }),
    },
  ];
  // Every customer has a catalogue (doc 91): it is scoped by the customer's code,
  // and a customer page's id is that code (`/customers/{code}`).
  tabs.push(
    {
      tab: "catalogue",
      route: "catalogue",
      label: "Catalogue",
      to: paths.catalogue({
        customerCode: customerCode ?? customerId,
        customerName: customerName ?? undefined,
      }),
    },
    {
      tab: "estimates",
      route: "estimates",
      label: "Estimates",
      to: paths.estimates({ customerId }),
    },
    {
      tab: "quotes",
      route: "customerQuotes",
      label: "Quotes",
      to: paths.customerQuotes({ customerId: pageKey }),
    },
    {
      tab: "orders",
      route: "customerOrders",
      label: "Orders",
      to: paths.customerOrders({ customerId: pageKey }),
    },
    {
      tab: "jobs",
      route: "jobs",
      label: "Jobs",
      to: paths.jobs({ customerId }),
    },
    {
      tab: "settings",
      route: "customerSettings",
      label: "Settings",
      to: paths.customerSettings({ customerId: pageKey }),
    },
    {
      tab: "invoices",
      route: "customerInvoices",
      label: "Invoices",
      to: paths.customerInvoices({ customerId: pageKey }),
    },
  );

  for (const section of plannedSectionsFor("customer", "tab")) {
    tabs.push({
      tab: section.key as PlannedTabKey<"customer">,
      route: "customerPlanned",
      label: section.label,
      to: paths.customerPlanned({
        customerId: pageKey,
        section: section.key as PlannedTabKey<"customer">,
      }),
      planned: true,
    });
  }

  const visible = tabs.filter((t) => isRouteEnabled(t.route));
  return (
    <nav
      aria-label="This customer's records"
      // One row that scrolls on a phone, as an object page's tabs do.
      className="flex gap-x-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--border)] [scrollbar-width:none]"
    >
      {visible.map(({ tab, label, to, planned }) => (
        <Link
          key={tab}
          to={to}
          aria-current={tab === current ? "page" : undefined}
          className={cn(
            "whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors",
            tab === current
              ? "border-primary font-medium text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
            planned && tab !== current && "italic text-muted-foreground/60",
          )}
        >
          {label}
          {planned && <span className="sr-only">, not built yet</span>}
        </Link>
      ))}
    </nav>
  );
}
